import { Component, EventEmitter, OnInit, Renderer2, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { OrganisationService } from 'src/app/organisation/organisation.service';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import { forkJoin } from 'rxjs';
import { KeycloakService } from 'src/app/authentication/keycloak.service';
import { saveAs } from 'file-saver';
import { MessageLevel, PgMessagesService } from '../../../../shared/services/pg-messages.service';
import { DocumentService } from '../../../../documents/documents.service';
import { PgTitleComponent, PgToolbarComponent } from '../../../../shared/components/title-bar/title-bar.component';
import { BunkerListToolbarComponent } from '../bunker-list-toolbar/bunker-list-toolbar.component';
import _ from 'lodash';
import { SharedModule } from 'src/app/shared/shared.module';
import { AgGridModule } from 'ag-grid-angular';
import { GridOptions, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { AgGridService } from 'src/app/shared/ag-grid/ag-grid.service';
import { UserLocalStoreService } from 'src/app/shared/services/user-local-store.service';
import { LuxonDateTimeHelpers } from 'src/app/shared/helpers/luxon-date-time.helpers';
import { BunkerServiceSpa } from 'src/app/generated/services/transportation/reports/bunkerSpa';
import { BunkeringDeliveryNote } from 'src/app/generated/services/transportation/reports/bunker';

@Component({
  selector: 'app-bunker-list',
  templateUrl: './bunker-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SharedModule, AgGridModule]
})
export class BunkerListComponent implements OnInit, PgTitleComponent, PgToolbarComponent {
  title: EventEmitter<string> = new EventEmitter();
  toolbarComponentType = BunkerListToolbarComponent;
  toolbarComponent: any;

  bunkeringDeliveryNote!: BunkeringDeliveryNote[];
  organisations!: Map<string, Organisation>;
  numberOfBunkering!: number;
  numberOfBunkeringLocations!: number;
  numberOfSuppliers!: number  ;
  biggestSupplierOrganisationId!: string;
  biggestSupplierProvidedTonnage!: number ;

  gridOptions: GridOptions = {};
  gridRestored: boolean = false;

  constructor(private _router: Router,
    private _documentsService: DocumentService,
    private _keycloakService: KeycloakService,
    private _organisationService: OrganisationService,
    private _pgMessagesService: PgMessagesService,
    private _renderer: Renderer2,
    private _userLocalStorage: UserLocalStoreService,
    private _bunkerService: BunkerServiceSpa,
  ) { }

  columnDefs = [
    {
      headerName: 'Actions',
      cellRenderer: (params: { data: { fileId: string; vesselName: any; bdnNumber: any; }; }) => {
        const container = this._renderer.createElement('div');

        if (params.data.fileId) {
          const downloadBtn = AgGridService.downloadButton(
            this._renderer,
            () => this.downloadFile(params.data.fileId, `${params.data.vesselName}-${params.data.bdnNumber}`),
          );
          this._renderer.appendChild(container, downloadBtn);
        }

        const editBtn = AgGridService.editButton(
          this._renderer,
          this._router,
          (_router, data) => this.editRecord(data),
          params.data
        );
        this._renderer.appendChild(container, editBtn);

        return container;
      }
    },

    { headerName: 'BDN NO', field: 'bdnNumber' },
    { headerName: 'Vessel', field: 'vesselName' },
    {
      headerName: 'Supplier', field: 'supplierOrganisationId',
      cellRenderer: (row: ICellRendererParams) => this.formatOrganisation(row.value)
    },
    {
      headerName: 'Location', field: 'locationOrganisationId',
      cellRenderer: (row: ICellRendererParams) => this.formatOrganisation(row.value)
    },
    { headerName: 'Product', field: 'product' },
    { headerName: 'Quantity', field: 'quantity' },
    {
      headerName: 'Date', field: 'date',
      cellRenderer: (row:ICellRendererParams) => LuxonDateTimeHelpers.offsetDateTimeToLocalString(row.data.date, 'yyyy-LL-dd')
    },
  ];

  ngOnInit() {
    this.gridOptions = AgGridService.getOptions();
    forkJoin([this._bunkerService.getAllBunkers({}),
      this._organisationService.getOrganisationIdToOrganisationMap()]).subscribe((value) => {
      this.bunkeringDeliveryNote = value[0]
        .bunkers
        .sort((a, b) => new Date(b.date?.isoDate || '').getTime() - new Date(a.date?.isoDate || '').getTime());
      this.organisations = value[1];
      this.numberOfBunkering = this.bunkeringDeliveryNote.length;
      this.numberOfBunkeringLocations = [...new Set(this.bunkeringDeliveryNote.map(b => b.locationOrganisationId))].length;
      this.numberOfSuppliers = [...new Set(this.bunkeringDeliveryNote.map(b => b.supplierOrganisationId))].length;
      const highest = _.chain(this.bunkeringDeliveryNote)
        .groupBy(b => b.supplierOrganisationId)
        .map((v, k) => ({
          supplierOrganisationId: k,
          tons: v.reduce((a, b) => a + b.quantity, 0)
        }))
        .sort((a, b) => b.tons - a.tons)
        .value()[0];
      this.biggestSupplierOrganisationId = highest.supplierOrganisationId;
      this.biggestSupplierProvidedTonnage = highest.tons;
    });
    this.title.emit('Bunker deliveries');
  }

  gridSetting(event: GridReadyEvent) {
    AgGridService.setting(event);
  }

  saveGridState(event: GridReadyEvent) {
    AgGridService.saveGridState(event, this.gridRestored, this._userLocalStorage, 'bunkerListGrid');
  }

  restoreGridState(event: GridReadyEvent) {
    this.gridRestored = AgGridService.restoreGridState(event, this._userLocalStorage, 'bunkerListGrid');
  }

  editRecord(bdn: BunkeringDeliveryNote) {
    if (bdn.date) {
      this._router.navigate(['vessels/bunker/', bdn.vesselImo, bdn.product, bdn.date.isoDate, bdn.bdnNumber]);}
  }

  hasRole(roleName: string) {
    return this._keycloakService.hasRole(roleName);
  }

  downloadFile(fileId: string, prefix: string) {
    this._documentsService.download(fileId).subscribe(b => {
      saveAs(b.body, `${prefix}.pdf`);
    }, () => {
      this._pgMessagesService.publishMessage({
        message: 'Could not download this file.',
        level: MessageLevel.Error,
        topic: 'Voyage document'
      });
    });
  }

  formatOrganisation(organisationId: string) {
    const foundOrganisation = this.organisations?.get(organisationId);
    return foundOrganisation ? foundOrganisation.nameTranslations['en'] : '';
  }
}
